/**
 * TZL-001 — Timezone (IANA) helpers for the DateTime field「年月日＋時間（TimeZone）」.
 *
 * Based on Intl (the browser ICU data is up to date), NOT on moment-timezone: the installed
 * moment-timezone (0.5.27) ships 2019 tz data, so some offsets would be wrong.
 * Logic mirrors BE: amplify/backend/function/amplifysnsdemocommonlibs/opt/common/TimezoneUtils.js
 *
 * Empty / invalid timezones fall back to Asia/Tokyo (Constant.DEFAULT_TIMEZONE).
 */
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;
const LOCAL_TEXT_PATTERN = /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2})$/;
const UTC_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

// Creating Intl.DateTimeFormat is costly → cache one formatter per timezone
const formatterCache = new Map();

const getFormatter = timeZone => {
    if (!formatterCache.has(timeZone)) {
        formatterCache.set(
            timeZone,
            new Intl.DateTimeFormat('en-US', {
                timeZone,
                hourCycle: 'h23',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            })
        );
    }
    return formatterCache.get(timeZone);
};

/**
 * Check whether the timezone is a valid IANA id
 * @param {string} timeZone
 * @returns {boolean}
 */
export const isValidTimeZone = timeZone => {
    if (typeof timeZone !== 'string' || timeZone.trim() === '') return false;
    try {
        getFormatter(timeZone);
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Return a valid timezone, falling back to Asia/Tokyo if empty/invalid
 * @param {string} timeZone
 * @returns {string}
 */
export const normalizeTimeZone = timeZone => (isValidTimeZone(timeZone) ? timeZone : Constant.DEFAULT_TIMEZONE);

/**
 * Browser timezone, '' when the browser cannot provide it
 * @returns {string}
 */
export const getBrowserTimeZone = () => {
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return isValidTimeZone(timeZone) ? timeZone : '';
    } catch (error) {
        return '';
    }
};

const getLocalParts = (date, timeZone) => {
    const parts = {};
    getFormatter(normalizeTimeZone(timeZone))
        .formatToParts(new Date(date))
        .forEach(part => {
            parts[part.type] = part.value;
        });

    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
        second: Number(parts.second)
    };
};

const padNumber = value => String(value).padStart(2, '0');

const formatLocalText = (date, timeZone) => {
    const local = getLocalParts(date, timeZone);
    return `${local.year}/${padNumber(local.month)}/${padNumber(local.day)} ${padNumber(local.hour)}:${padNumber(local.minute)}`;
};

/**
 * Offset (minutes) of the timezone from UTC at an instant. E.g. Asia/Tokyo → 540
 * @param {string} timeZone
 * @param {Date|number|string} date
 * @returns {number}
 */
export const getOffset = (timeZone, date) => {
    const time = new Date(date).getTime();
    const timeWithoutMs = time - (((time % 1000) + 1000) % 1000);
    const local = getLocalParts(timeWithoutMs, timeZone);
    const localAsUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    return Math.round((localAsUtc - timeWithoutMs) / MS_PER_MINUTE);
};

/**
 * Offset label (UTC±HH:MM) of the timezone at an instant (DST-aware)
 * @param {string} timeZone
 * @param {Date|number|string} date
 * @returns {string} e.g. '(UTC+09:00)'
 */
export const getOffsetLabel = (timeZone, date) => {
    const offsetMinutes = getOffset(normalizeTimeZone(timeZone), date);
    const sign = offsetMinutes < 0 ? '-' : '+';
    const absMinutes = Math.abs(offsetMinutes);
    return `(UTC${sign}${padNumber(Math.floor(absMinutes / 60))}:${padNumber(absMinutes % 60)})`;
};

/**
 * Convert a local date time text in the given timezone to a UTC ISO string.
 * - Time repeated twice due to DST fall-back → first occurrence.
 * - Time that does not exist due to DST spring-forward → interpreted with the offset before the transition (Q10).
 * @param {string} localText - 'YYYY/MM/DD HH:mm'
 * @param {string} timeZone
 * @returns {string} UTC ISO string, '' when localText is invalid
 */
export const convertLocalToUtc = (localText, timeZone) => {
    const text = typeof localText === 'string' ? localText.trim() : '';
    const matched = LOCAL_TEXT_PATTERN.exec(text);
    if (!matched) return '';

    const validTimeZone = normalizeTimeZone(timeZone);
    const [, year, month, day, hour, minute] = matched.map(Number);
    const localAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    // Invalid date/time (e.g. 2026/02/30, 24:00) → Date.UTC rolls over, so it does not match the original text
    if (formatLocalText(localAsUtc, 'UTC') !== text) return '';

    // Candidate offsets around this time (covering one DST transition)
    const offsetBefore = getOffset(validTimeZone, localAsUtc - MS_PER_DAY);
    const candidateOffsets = new Set([offsetBefore, getOffset(validTimeZone, localAsUtc), getOffset(validTimeZone, localAsUtc + MS_PER_DAY)]);
    const validTimes = [...candidateOffsets]
        .map(offset => localAsUtc - offset * MS_PER_MINUTE)
        .filter(time => formatLocalText(time, validTimeZone) === text);

    const utcTime = validTimes.length > 0 ? Math.min(...validTimes) : localAsUtc - offsetBefore * MS_PER_MINUTE;
    return new Date(utcTime).toISOString();
};

/**
 * Format a UTC ISO string in the given timezone
 * @param {string} utcValue - UTC ISO string
 * @param {string} timeZone
 * @param {boolean} isShowOffset - append ' (UTC±HH:MM)'
 * @returns {string} 'YYYY/MM/DD HH:mm' or 'YYYY/MM/DD HH:mm (UTC±HH:MM)', '' when utcValue is empty/invalid
 */
export const formatUtcToLocal = (utcValue, timeZone, isShowOffset = false) => {
    // Only a stored UTC ISO string is converted (Date.parse would also accept local texts such as '2026/09/08 14:00')
    if (!isUtcIsoString(utcValue)) return '';
    const time = Date.parse(utcValue);

    const validTimeZone = normalizeTimeZone(timeZone);
    const localText = formatLocalText(time, validTimeZone);
    return isShowOffset ? `${localText} ${getOffsetLabel(validTimeZone, time)}` : localText;
};

/**
 * Check whether the value is a UTC ISO string as produced by Date.toISOString() (e.g. '2026-09-08T05:00:00.000Z')
 * — the stored value of the DateTime field「年月日＋時間（TimeZone）」
 * @param {string} value
 * @returns {boolean}
 */
export const isUtcIsoString = value => {
    if (typeof value !== 'string' || !UTC_ISO_PATTERN.test(value)) return false;
    const time = Date.parse(value);
    // Round trip rejects invalid dates such as 2026-02-30T00:00:00.000Z
    return !Number.isNaN(time) && new Date(time).toISOString() === value;
};

/**
 * Get today's date string (YYYY/MM/DD) as seen in a specific IANA timezone.
 * Used in AGO mode date filter to compute "today" from the user's perspective (TZL-001).
 * Mirrors BE: amplify/backend/function/amplifysnsdemocommonlibs/opt/common/TimezoneUtils.js getLocalTodayString.
 *
 * @param {string} timeZone - IANA timezone string
 * @returns {string} Date string in 'YYYY/MM/DD' format
 */
export const getLocalTodayString = timeZone => {
    const local = getLocalParts(new Date(), timeZone);
    return `${local.year}/${padNumber(local.month)}/${padNumber(local.day)}`;
};

/**
 * Get Monday of the current week as seen in a specific IANA timezone.
 * Used in AGO mode "0 weeks ago" calculation (TZL-001).
 * Mirrors BE: amplify/backend/function/amplifysnsdemocommonlibs/opt/common/TimezoneUtils.js getLocalMondayString.
 *
 * @param {string} timeZone - IANA timezone string
 * @returns {string} Date string in 'YYYY/MM/DD' format
 */
export const getLocalMondayString = timeZone => {
    const todayStr = getLocalTodayString(timeZone);
    const today = new Date(`${todayStr.replace(/\//g, '-')}T00:00:00Z`);
    // getUTCDay(): 0=Sun, 1=Mon, ...
    const dayOfWeek = today.getUTCDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(today.getTime() - daysFromMonday * MS_PER_DAY);
    return `${monday.getUTCFullYear()}/${padNumber(monday.getUTCMonth() + 1)}/${padNumber(monday.getUTCDate())}`;
};

/**
 * Get the date string (YYYY/MM/DD) N days/weeks before "today" as seen in a specific IANA timezone.
 * Used in AGO mode date filter (TZL-001).
 *
 * @param {number} agoValue - number of days/weeks ago
 * @param {number} agoUnitTimeMs - ms per unit (1 day or 1 week)
 * @param {string} timeZone - IANA timezone string
 * @returns {string} Date string in 'YYYY/MM/DD' format
 */
export const getLocalAgoDateString = (agoValue, agoUnitTimeMs, timeZone) => {
    const todayStr = getLocalTodayString(timeZone);
    const today = new Date(`${todayStr.replace(/\//g, '-')}T00:00:00Z`);
    const agoDate = new Date(today.getTime() - agoValue * agoUnitTimeMs);
    return `${agoDate.getUTCFullYear()}/${padNumber(agoDate.getUTCMonth() + 1)}/${padNumber(agoDate.getUTCDate())}`;
};

/**
 * Resolve the IANA timezone string for the current user.
 *
 * - useAutoTimezone === false AND userSetting.timeZone is a valid IANA id  →  userSetting.timeZone
 * - otherwise (useAutoTimezone !== false, or timeZone is invalid/empty)    →  browser timezone > DEFAULT_TIMEZONE
 *
 * @param {object|string} userSetting - raw userSetting value from User record (AWSJSON or parsed)
 * @returns {string} IANA timezone string, e.g. 'Asia/Tokyo'
 */
export const resolveUserTimezone = (userSetting) => {
    try {
        const setting = Utils.convertJSONDataToObject(userSetting) || {};

        if (setting.useAutoTimezone === false && isValidTimeZone(setting.timeZone)) {
            return setting.timeZone;
        }

        return getBrowserTimeZone() || Constant.DEFAULT_TIMEZONE;
    } catch (_) {
        return Constant.DEFAULT_TIMEZONE;
    }
};

/**
 * Returns the browser's IANA timezone string for use on public pages (no user auth).
 * Falls back to DEFAULT_TIMEZONE if the Intl API is unavailable.
 *
 * @returns {string} IANA timezone string, e.g. 'Asia/Tokyo'
 */
export const getPublicPageTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || Constant.DEFAULT_TIMEZONE;
    } catch (_) {
        return Constant.DEFAULT_TIMEZONE;
    }
};

/**
 * Format a UTC ISO timestamp according to the user's timezone setting.
 * Uses Intl (consistent with the rest of this module — no moment-timezone dependency).
 *
 * @param {string} timestamp - UTC ISO timestamp string
 * @param {string} format - moment.js-style format token, e.g. 'YYYY/MM/DD HH:mm' or 'YYYY/MM/DD HH:mm:ss'
 * @param {object|string} userSetting - raw userSetting value from User record
 * @returns {string} formatted datetime string in user's timezone
 */
export const formatUserDateTime = (timestamp, format, userSetting) => {
    if (!timestamp) return '';
    const tz = resolveUserTimezone(userSetting);
    const time = new Date(timestamp).getTime();
    if (Number.isNaN(time)) return '';
    const local = getLocalParts(time, tz);
    return format
        .replace('YYYY', String(local.year))
        .replace('MM', padNumber(local.month))
        .replace('DD', padNumber(local.day))
        .replace('HH', padNumber(local.hour))
        .replace('mm', padNumber(local.minute))
        .replace('ss', padNumber(local.second));
};

/**
 * Format a UTC ISO timestamp according to the user's timezone setting,
 * appending the UTC offset label e.g. "(UTC+09:00)" after the formatted string.
 * Used for fields that require the offset to be shown (BR-48).
 *
 * @param {string} timestamp - UTC ISO timestamp string
 * @param {string} format - moment.js-style format token, e.g. 'YYYY/MM/DD HH:mm:ss'
 * @param {object|string} userSetting - raw userSetting value from User record
 * @returns {string} formatted datetime string with offset label, e.g. '2026/01/15 14:00:00 (UTC+09:00)'
 */
export const formatUserDateTimeWithOffset = (timestamp, format, userSetting) => {
    if (!timestamp) return '';
    const tz = resolveUserTimezone(userSetting);
    const formatted = formatUserDateTime(timestamp, format, userSetting);
    if (!formatted) return '';
    return `${formatted} ${getOffsetLabel(tz, new Date(timestamp).getTime())}`;
};

const TimezoneUtils = {
    isValidTimeZone,
    normalizeTimeZone,
    getBrowserTimeZone,
    getOffset,
    getOffsetLabel,
    convertLocalToUtc,
    formatUtcToLocal,
    isUtcIsoString,
    resolveUserTimezone,
    getPublicPageTimezone,
    formatUserDateTimeWithOffset,
    formatUserDateTime,
    getLocalTodayString,
    getLocalMondayString,
    getLocalAgoDateString
};

export default TimezoneUtils;
